import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import API from "../services/api";
import { resolveProfilePhoto } from "../utils/profilePhoto";
import "./ContractorPages.css";

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString("en-IN") : "—");
const fmtDateTime = (value) => (value ? new Date(value).toLocaleString("en-IN") : "—");
const fmtMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const bagSummary = (rows, unit) => {
  const summary = Array.isArray(rows) ? rows.map((row) => `${row.bagSize}`).join(", ") : "";
  return summary || String(unit || "").trim() || "—";
};

const statusMeta = {
  pending_plant_approval: { label: "Pending Plant Approval", tone: "warning" },
  plant_approved: { label: "Plant Approved", tone: "success" },
  plant_rejected: { label: "Plant Rejected", tone: "danger" },
};

const queryStatusMeta = {
  open: { label: "Open Query", tone: "danger" },
  clarified: { label: "Clarified", tone: "success" },
};

const paymentMeta = {
  pending: { label: "Pending Payment", tone: "warning" },
  paid: { label: "Paid", tone: "success" },
};
const paymentReceiptMeta = {
  pending_confirmation: { label: "Awaiting Contractor", tone: "warning" },
  received: { label: "Received Confirmed", tone: "success" },
};

const statusBadge = (status) => {
  const meta = statusMeta[status] || { label: status || "—", tone: "secondary" };
  return <span className={`badge text-bg-${meta.tone}`}>{meta.label}</span>;
};

const paymentBadge = (status) => {
  const meta = paymentMeta[status] || paymentMeta.pending;
  return <span className={`badge text-bg-${meta.tone}`}>{meta.label}</span>;
};

const paymentReceiptBadge = (status) => {
  const meta = paymentReceiptMeta[status] || paymentReceiptMeta.pending_confirmation;
  return <span className={`badge text-bg-${meta.tone}`}>{meta.label}</span>;
};

const queryBadge = (status) => {
  if (!status) return null;
  const meta = queryStatusMeta[status] || { label: status, tone: "secondary" };
  return <span className={`badge text-bg-${meta.tone}`}>{meta.label}</span>;
};

const roleLabel = (role) => {
  if (role === "plant_manager") return "Plant Manager";
  if (role === "contractor") return "Contractor";
  return role || "User";
};
const paymentProofName = (url = "", index = 0) => {
  const clean = String(url || "").split("/").pop() || "";
  return clean || `Proof ${index + 1}`;
};

const ReviewQueryCell = ({ row }) => {
  const hasAccounts = row.accountsQueryNote;
  const hasHr = row.hrQueryNote;
  if (!hasAccounts && !hasHr) return "—";

  return (
    <div className="contractor-inline-stack">
      {hasAccounts ? (
        <div className="contractor-query-block">
          <strong>Accounts</strong>
          <div>{row.accountsQueryNote}</div>
          <div className="small text-muted">
            {row.accountsQueryBy?.fullName || "—"} • {fmtDateTime(row.accountsQueryAt)}
          </div>
          {queryBadge(row.accountsQueryStatus)}
        </div>
      ) : null}
      {hasHr ? (
        <div className="contractor-query-block">
          <strong>HR</strong>
          <div>{row.hrQueryNote}</div>
          <div className="small text-muted">
            {row.hrQueryBy?.fullName || "—"} • {fmtDateTime(row.hrQueryAt)}
          </div>
          {queryBadge(row.hrQueryStatus)}
        </div>
      ) : null}
    </div>
  );
};

const ClarificationCell = ({ row }) => {
  const items = Array.isArray(row.clarifications) ? row.clarifications : [];
  if (!items.length) return "—";

  return (
    <div className="contractor-inline-stack">
      {items.map((item, index) => (
        <div className="contractor-query-block" key={`${row._id}-clar-${index}`}>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <strong>{roleLabel(item.submittedRole)}</strong>
            <span className="small text-muted">{item.submittedName || "—"}</span>
            <span className="small text-muted">{item.scope === "accounts" ? "Accounts" : "HR"}</span>
          </div>
          {item.message ? <div>{item.message}</div> : null}
          <div className="small text-muted">{fmtDateTime(item.createdAt)}</div>
          {Array.isArray(item.photos) && item.photos.length ? (
            <div className="contractor-clar-photo-row">
              {item.photos.map((photo, photoIndex) => {
                const src = resolveProfilePhoto(photo, item.createdAt || photoIndex);
                return (
                  <a href={src} target="_blank" rel="noreferrer" key={`${row._id}-photo-${photoIndex}`}>
                    <img src={src} alt="clarification" className="contractor-clar-photo" />
                  </a>
                );
              })}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

const ContractorWorkApprovalPage = ({
  title,
  subtitle,
  endpoint,
  canApprove = false,
  canQuery = false,
  canClarify = false,
  canProcessPayment = false,
  reviewScope = "",
  defaultStatus = "",
  infoMessage = "",
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    workType: "",
    workflowStatus: defaultStatus,
  });
  const [queryModal, setQueryModal] = useState({ show: false, row: null, note: "" });
  const [paymentModal, setPaymentModal] = useState({
    show: false,
    row: null,
    paymentAmount: "",
    paymentMode: "",
    paymentPaidAt: "",
    paymentReference: "",
    paymentNote: "",
    paymentProofs: [],
  });
  const [clarModal, setClarModal] = useState({ show: false, row: null, scope: reviewScope || "accounts", message: "", photos: [] });

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await API.get(endpoint, { params: filters });
      setRows(Array.isArray(res.data?.entries) ? res.data.entries : []);
      setSummary(res.data?.summary || {});
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load contractor work entries");
      setRows([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const totals = useMemo(
    () => rows.reduce((acc, row) => acc + Number(row.totalAmount || 0), 0),
    [rows]
  );
  const paidTotal = useMemo(
    () => rows.filter((row) => row.paymentStatus === "paid").reduce((acc, row) => acc + Number(row.paymentAmount || row.totalAmount || 0), 0),
    [rows]
  );
  const pendingPaymentTotal = useMemo(
    () => rows.filter((row) => row.paymentStatus !== "paid").reduce((acc, row) => acc + Number(row.totalAmount || 0), 0),
    [rows]
  );

  const updateStatus = async (row, status) => {
    const notePrompt =
      status === "plant_rejected"
        ? "Reject note likhiye"
        : "Approval note likhiye (optional)";
    const note = window.prompt(notePrompt, row.plantApprovalNote || "");
    if (note === null) return;

    try {
      setMessage("");
      setError("");
      await API.patch(`${endpoint}/${row._id}/status`, { status, note });
      setMessage(status === "plant_approved" ? "Entry approved and sent to Accounts + HR." : "Entry rejected.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update status");
    }
  };

  const openQueryModal = (row) => {
    const existingNote = reviewScope === "accounts" ? row.accountsQueryNote : row.hrQueryNote;
    setQueryModal({ show: true, row, note: existingNote || "" });
  };

  const saveQuery = async () => {
    if (!queryModal.row?._id) return;
    try {
      setSaving(true);
      setError("");
      await API.patch(`${endpoint}/${queryModal.row._id}/query`, { note: queryModal.note });
      setMessage(`${reviewScope === "accounts" ? "Accounts" : "HR"} query saved.`);
      setQueryModal({ show: false, row: null, note: "" });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save query");
    } finally {
      setSaving(false);
    }
  };

  const openPaymentModal = (row) => {
    setPaymentModal({
      show: true,
      row,
      paymentAmount: String(row.paymentAmount || row.totalAmount || ""),
      paymentMode: row.paymentMode || "",
      paymentPaidAt: row.paymentPaidAt ? new Date(row.paymentPaidAt).toISOString().slice(0, 10) : "",
      paymentReference: row.paymentReference || "",
      paymentNote: row.paymentNote || "",
      paymentProofs: [],
    });
  };

  const savePayment = async () => {
    if (!paymentModal.row?._id) return;
    try {
      setSaving(true);
      setError("");
      const data = new FormData();
      data.append("paymentAmount", paymentModal.paymentAmount);
      data.append("paymentMode", paymentModal.paymentMode);
      data.append("paymentPaidAt", paymentModal.paymentPaidAt);
      data.append("paymentReference", paymentModal.paymentReference);
      data.append("paymentNote", paymentModal.paymentNote);
      Array.from(paymentModal.paymentProofs || []).forEach((file) => data.append("paymentProofs", file));
      await API.patch(`${endpoint}/${paymentModal.row._id}/payment`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessage("Payment processed.");
      setPaymentModal({
        show: false,
        row: null,
        paymentAmount: "",
        paymentMode: "",
        paymentPaidAt: "",
        paymentReference: "",
        paymentNote: "",
        paymentProofs: [],
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to process payment");
    } finally {
      setSaving(false);
    }
  };

  const openClarificationModal = (row) => {
    const scopes = [];
    if (row.accountsQueryNote) scopes.push("accounts");
    if (row.hrQueryNote) scopes.push("hr");
    setClarModal({
      show: true,
      row,
      scope: scopes.includes(reviewScope) ? reviewScope : (scopes[0] || "accounts"),
      message: "",
      photos: [],
    });
  };

  const saveClarification = async () => {
    if (!clarModal.row?._id) return;
    try {
      setSaving(true);
      setError("");
      const data = new FormData();
      data.append("scope", clarModal.scope);
      data.append("message", clarModal.message);
      Array.from(clarModal.photos || []).forEach((file) => data.append("photos", file));
      await API.post(`${endpoint}/${clarModal.row._id}/clarifications`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessage("Clarification submitted.");
      setClarModal({ show: false, row: null, scope: reviewScope || "accounts", message: "", photos: [] });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to submit clarification");
    } finally {
      setSaving(false);
    }
  };

  const canShowClarifyButton = (row) => canClarify && (row.accountsQueryNote || row.hrQueryNote);
  const canShowPaymentButton = (row) =>
    canProcessPayment &&
    row.workflowStatus === "plant_approved" &&
    row.accountsQueryStatus !== "open" &&
    row.hrQueryStatus !== "open";

  return (
    <>
      <Card className="contractor-card">
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h4 className="mb-1">{title}</h4>
              <div className="text-muted">{subtitle}</div>
            </div>
            <Button variant="outline-secondary" onClick={load} disabled={loading}>
              Refresh
            </Button>
          </div>

          {infoMessage ? <Alert variant="info">{infoMessage}</Alert> : null}
          {message ? <Alert variant="success">{message}</Alert> : null}
          {error ? <Alert variant="danger">{error}</Alert> : null}

          <Row className="g-2 mb-3">
            <Col md={3}>
              <Form.Control type="date" name="from" value={filters.from} onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Control type="date" name="to" value={filters.to} onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))} />
            </Col>
            <Col md={3}>
              <Form.Control
                name="workType"
                placeholder="Work type"
                value={filters.workType}
                onChange={(e) => setFilters((prev) => ({ ...prev, workType: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.workflowStatus}
                onChange={(e) => setFilters((prev) => ({ ...prev, workflowStatus: e.target.value }))}
              >
                <option value="">All Status</option>
                <option value="pending_plant_approval">Pending Plant Approval</option>
                <option value="plant_approved">Plant Approved</option>
                <option value="plant_rejected">Plant Rejected</option>
              </Form.Select>
            </Col>
            <Col md={12} className="d-flex gap-3 flex-wrap align-items-center">
              <Button onClick={load} disabled={loading}>{loading ? "Loading..." : "Apply Filter"}</Button>
              <div className="text-muted">
                Total Amount: <strong>{fmtMoney(totals)}</strong>
              </div>
              <div className="text-muted">
                Pending: <strong>{summary.pending_plant_approval || 0}</strong>
              </div>
              <div className="text-muted">
                Approved: <strong>{summary.plant_approved || 0}</strong>
              </div>
              <div className="text-muted">
                Paid: <strong>{fmtMoney(paidTotal)}</strong>
              </div>
              <div className="text-muted">
                Payment Pending: <strong>{fmtMoney(pendingPaymentTotal)}</strong>
              </div>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-muted">No contractor work entries found.</div>
          ) : (
            <>
              <div className="contractor-table-wrap">
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Contractor</th>
                      <th>Worker</th>
                      <th>Work Type</th>
                      <th>No. Of Bags</th>
                      <th>Bag Details</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Review Query</th>
                      <th>Clarifications</th>
                      <th>Payment</th>
                      <th>Approved By</th>
                      {canApprove || canQuery || canClarify || canProcessPayment ? <th>Action</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row._id}>
                        <td>{fmtDate(row.workDate)}</td>
                        <td>{row.contractorProfile?.contractorName || row.contractorUser?.fullName || "—"}</td>
                        <td>{row.workerName || "—"}</td>
                        <td>{row.workType || "—"}</td>
                        <td>{row.quantity}</td>
                        <td>{bagSummary(row.bagDetails, row.unit)}</td>
                        <td>{fmtMoney(row.totalAmount)}</td>
                        <td>{statusBadge(row.workflowStatus)}</td>
                        <td><ReviewQueryCell row={row} /></td>
                        <td><ClarificationCell row={row} /></td>
                        <td>
                          {paymentBadge(row.paymentStatus)}
                          {row.paymentProcessedBy?.fullName ? <div className="small mt-1">{row.paymentProcessedBy.fullName}</div> : null}
                          {row.paymentProcessedAt ? <div className="small text-muted">{fmtDateTime(row.paymentProcessedAt)}</div> : null}
                          {row.paymentPaidAt ? <div className="small text-muted">Paid on: {fmtDateTime(row.paymentPaidAt)}</div> : null}
                          {row.paymentAmount ? <div className="small text-muted">Amount: {fmtMoney(row.paymentAmount)}</div> : null}
                          {row.paymentMode ? <div className="small text-muted">Mode: {row.paymentMode}</div> : null}
                          {row.paymentReference ? <div className="small text-muted">Ref: {row.paymentReference}</div> : null}
                          {row.paymentProofs?.length ? (
                            <div className="small mt-1 d-flex flex-column gap-1">
                              {row.paymentProofs.map((proof, proofIndex) => (
                                <a key={`${row._id}-proof-${proofIndex}`} href={proof} target="_blank" rel="noreferrer">
                                  {paymentProofName(proof, proofIndex)}
                                </a>
                              ))}
                            </div>
                          ) : null}
                          {row.paymentStatus === "paid" ? (
                            <div className="small mt-1">
                              {paymentReceiptBadge(row.paymentReceiptStatus)}
                              {row.paymentReceivedAt ? <div className="text-muted mt-1">{fmtDateTime(row.paymentReceivedAt)}</div> : null}
                              {row.paymentReceivedNote ? <div className="text-muted mt-1">{row.paymentReceivedNote}</div> : null}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {row.plantApprovedBy?.fullName || "—"}
                          {row.plantApprovedAt ? <div className="small text-muted">{fmtDateTime(row.plantApprovedAt)}</div> : null}
                        </td>
                        {canApprove || canQuery || canClarify || canProcessPayment ? (
                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              {canApprove ? (
                                <>
                                  <Button size="sm" variant="success" onClick={() => updateStatus(row, "plant_approved")}>
                                    Approve
                                  </Button>
                                  <Button size="sm" variant="outline-danger" onClick={() => updateStatus(row, "plant_rejected")}>
                                    Reject
                                  </Button>
                                </>
                              ) : null}
                              {canQuery ? (
                                <Button size="sm" variant="outline-warning" onClick={() => openQueryModal(row)} disabled={row.workflowStatus !== "plant_approved"}>
                                  Raise Query
                                </Button>
                              ) : null}
                              {canShowClarifyButton(row) ? (
                                <Button size="sm" variant="outline-primary" onClick={() => openClarificationModal(row)}>
                                  Submit Clarity
                                </Button>
                              ) : null}
                              {canShowPaymentButton(row) ? (
                                <Button size="sm" variant="primary" onClick={() => openPaymentModal(row)}>
                                  Mark Paid
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="contractor-mobile-list">
                {rows.map((row) => (
                  <div className="contractor-mobile-item" key={row._id}>
                    <div className="title">{row.contractorProfile?.contractorName || row.contractorUser?.fullName || "—"}</div>
                    <div className="meta">{fmtDate(row.workDate)} | {row.workerName} | {row.workType}</div>
                    <div className="meta">{row.quantity} bags | {fmtMoney(row.totalAmount)}</div>
                    <div className="meta">Bags: {bagSummary(row.bagDetails, row.unit)}</div>
                    <div className="meta">Status: {statusMeta[row.workflowStatus]?.label || row.workflowStatus}</div>
                    <div className="meta">Payment: {paymentMeta[row.paymentStatus || "pending"]?.label || "Pending Payment"}</div>
                    {row.paymentAmount ? <div className="meta">Paid Amount: {fmtMoney(row.paymentAmount)}</div> : null}
                    {row.paymentMode ? <div className="meta">Mode: {row.paymentMode}</div> : null}
                    {row.paymentStatus === "paid" ? (
                      <div className="meta">
                        Receipt: {paymentReceiptMeta[row.paymentReceiptStatus || "pending_confirmation"]?.label || "Awaiting Contractor"}
                      </div>
                    ) : null}
                    {row.paymentProofs?.length ? (
                      <div className="meta">
                        Proofs: {row.paymentProofs.map((proof, proofIndex) => (
                          <a key={`${row._id}-mobile-proof-${proofIndex}`} href={proof} target="_blank" rel="noreferrer" className="d-inline-block me-2">
                            {paymentProofName(proof, proofIndex)}
                          </a>
                        ))}
                      </div>
                    ) : null}
                    {row.paymentReference ? <div className="meta">Ref: {row.paymentReference}</div> : null}
                    {row.paymentReceivedAt ? <div className="meta">Received At: {fmtDateTime(row.paymentReceivedAt)}</div> : null}
                    {row.accountsQueryNote || row.hrQueryNote ? <div className="meta"><ReviewQueryCell row={row} /></div> : null}
                    {Array.isArray(row.clarifications) && row.clarifications.length ? <div className="meta"><ClarificationCell row={row} /></div> : null}
                    <div className="d-flex gap-2 flex-wrap mt-2">
                      {canApprove ? (
                        <>
                          <Button size="sm" variant="success" onClick={() => updateStatus(row, "plant_approved")}>Approve</Button>
                          <Button size="sm" variant="outline-danger" onClick={() => updateStatus(row, "plant_rejected")}>Reject</Button>
                        </>
                      ) : null}
                      {canQuery ? (
                        <Button size="sm" variant="outline-warning" onClick={() => openQueryModal(row)} disabled={row.workflowStatus !== "plant_approved"}>
                          Raise Query
                        </Button>
                      ) : null}
                      {canShowClarifyButton(row) ? (
                        <Button size="sm" variant="outline-primary" onClick={() => openClarificationModal(row)}>
                          Submit Clarity
                        </Button>
                      ) : null}
                      {canShowPaymentButton(row) ? (
                        <Button size="sm" variant="primary" onClick={() => openPaymentModal(row)}>
                          {row.paymentStatus === "paid" ? "Update Payment" : "Mark Paid"}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={queryModal.show} onHide={() => setQueryModal({ show: false, row: null, note: "" })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Raise Query</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Query Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={queryModal.note}
              onChange={(e) => setQueryModal((prev) => ({ ...prev, note: e.target.value }))}
              placeholder="Issue kya hai likhiye"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setQueryModal({ show: false, row: null, note: "" })}>Cancel</Button>
          <Button onClick={saveQuery} disabled={saving || !queryModal.note.trim()}>
            {saving ? "Saving..." : "Save Query"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={paymentModal.show}
        onHide={() => setPaymentModal({
          show: false,
          row: null,
          paymentAmount: "",
          paymentMode: "",
          paymentPaidAt: "",
          paymentReference: "",
          paymentNote: "",
          paymentProofs: [],
        })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Process Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="small text-muted">Entry Amount</div>
            <strong>{fmtMoney(paymentModal.row?.totalAmount || 0)}</strong>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Paid Amount</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={paymentModal.paymentAmount}
              onChange={(e) => setPaymentModal((prev) => ({ ...prev, paymentAmount: e.target.value }))}
              placeholder="How much paid"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Payment Mode</Form.Label>
            <Form.Select
              value={paymentModal.paymentMode}
              onChange={(e) => setPaymentModal((prev) => ({ ...prev, paymentMode: e.target.value }))}
            >
              <option value="">Select mode</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
              <option value="other">Other</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Paid Date</Form.Label>
            <Form.Control
              type="date"
              value={paymentModal.paymentPaidAt}
              onChange={(e) => setPaymentModal((prev) => ({ ...prev, paymentPaidAt: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Payment Reference</Form.Label>
            <Form.Control
              value={paymentModal.paymentReference}
              onChange={(e) => setPaymentModal((prev) => ({ ...prev, paymentReference: e.target.value }))}
              placeholder="UTR / Voucher / Cash ref"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Payment Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={paymentModal.paymentNote}
              onChange={(e) => setPaymentModal((prev) => ({ ...prev, paymentNote: e.target.value }))}
              placeholder="Optional note"
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Payment Proofs</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*,application/pdf"
              onChange={(e) => setPaymentModal((prev) => ({ ...prev, paymentProofs: Array.from(e.target.files || []) }))}
            />
            <div className="small text-muted mt-1">Screenshot, UTR slip, cheque image, ya PDF upload kar sakte ho.</div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setPaymentModal({
              show: false,
              row: null,
              paymentAmount: "",
              paymentMode: "",
              paymentPaidAt: "",
              paymentReference: "",
              paymentNote: "",
              paymentProofs: [],
            })}
          >
            Cancel
          </Button>
          <Button onClick={savePayment} disabled={saving || !Number(paymentModal.paymentAmount || 0)}>
            {saving ? "Saving..." : "Save Payment"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={clarModal.show} onHide={() => setClarModal({ show: false, row: null, scope: reviewScope || "accounts", message: "", photos: [] })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Submit Clarification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>For</Form.Label>
            <Form.Select value={clarModal.scope} onChange={(e) => setClarModal((prev) => ({ ...prev, scope: e.target.value }))}>
              {clarModal.row?.accountsQueryNote ? <option value="accounts">Accounts</option> : null}
              {clarModal.row?.hrQueryNote ? <option value="hr">HR</option> : null}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={clarModal.message}
              onChange={(e) => setClarModal((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Clearity yahan likhiye"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Photos</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setClarModal((prev) => ({ ...prev, photos: Array.from(e.target.files || []) }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setClarModal({ show: false, row: null, scope: reviewScope || "accounts", message: "", photos: [] })}>Cancel</Button>
          <Button onClick={saveClarification} disabled={saving || (!clarModal.message.trim() && !(clarModal.photos || []).length)}>
            {saving ? "Saving..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ContractorWorkApprovalPage;
